import { Action, createReducer, on } from '@ngrx/store';
import { ConversationSummary } from '../../domain/models/conversation-summary.model';
import * as ConversationActions from './conversation.actions';
import { createInitialConversationState, initialConversationState } from './conversation.state';
import { ConversationState } from './conversation.state';

const containsConversation = (list: readonly ConversationSummary[], conversationId: string): boolean =>
  list.some(
    (conversationSummary) =>
      conversationSummary.conversationId === conversationId || conversationSummary.id === conversationId,
  );

// Selection survives while the conversation lives in either history or saved conversations.
const resolveSelectedConversationId = (
  selectedConversationId: string | null,
  conversationHistory: readonly ConversationSummary[],
  savedConversations: readonly ConversationSummary[],
): string | null =>
  selectedConversationId &&
  (containsConversation(conversationHistory, selectedConversationId) ||
    containsConversation(savedConversations, selectedConversationId))
    ? selectedConversationId
    : null;

const conversationReducerInternal = createReducer(
  initialConversationState,
  on(ConversationActions.setActiveConversation, (state, { activeConversation }) => ({
    ...state,
    activeConversation,
  })),
  on(ConversationActions.setConversationHistory, (state, { conversationHistory }) => ({
    ...state,
    conversationHistory,
    selectedConversationId: resolveSelectedConversationId(
      state.selectedConversationId,
      conversationHistory,
      state.savedConversations,
    ),
  })),
  on(ConversationActions.setSavedConversations, (state, { savedConversations }) => ({
    ...state,
    savedConversations,
    selectedConversationId: resolveSelectedConversationId(
      state.selectedConversationId,
      state.conversationHistory,
      savedConversations,
    ),
  })),
  on(ConversationActions.setSelectedConversationId, (state, { selectedConversationId }) => ({
    ...state,
    selectedConversationId,
  })),
  on(ConversationActions.setQuickQuestions, (state, { quickQuestions }) => ({
    ...state,
    quickQuestions,
  })),
  on(ConversationActions.setLoading, (state, { loading }) => ({
    ...state,
    loading,
  })),
  on(ConversationActions.setSendingMessage, (state, { sendingMessage }) => ({
    ...state,
    sendingMessage,
  })),
  on(ConversationActions.setError, (state, { error }) => ({
    ...state,
    error,
  })),
  on(ConversationActions.initializeConversationState, () => createInitialConversationState()),
  on(ConversationActions.resetConversationState, () => initialConversationState),
);

export function conversationReducer(state: ConversationState | undefined, action: Action): ConversationState {
  return conversationReducerInternal(state, action);
}
